import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { hasPermission } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { UsageService } from "@/services/usageService";
import UsageToolbar from "@/components/usage/UsageToolbar";
import { windowParams } from "@/components/usage/useTenantUsage";
import UsageSummary from "@/components/usage/UsageSummary";
import UsageByUserTable from "@/components/usage/UsageByUserTable";
import { CenteredSpinner } from "@/components/shared/Loader";

/**
 * The tenant's own usage.
 *
 * Two permissions, two sections:
 *
 * - `usage.view` opens the page and the totals.
 * - `usage.view_users` additionally shows the per-user breakdown. Separate on
 *   purpose so a lead can be given consumption figures without also being given
 *   visibility into who did what.
 *
 * Observation only — there are no quotas, caps or limits anywhere in this
 * feature, so nothing here can be exceeded or blocked.
 */
const AdminUsage = () => {
  const setHeader = useOutletContext();
  const { user } = useAuth();
  const canViewUsers = hasPermission(PERMISSIONS.USAGE_VIEW_USERS, user?.permissions);

  const [range, setRange] = useState({ from: "", to: "" });
  const [summary, setSummary] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    setHeader({ title: "Usage", subTitle: "What your organization has consumed" });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  // `windowParams` is shared with the SuperAdmin screens: `to` is exclusive
  // server-side, so a date picked as "to" is turned into the start of the
  // *next* day — otherwise picking today would silently exclude everything
  // that happened today.
  const toParams = useCallback(() => windowParams(range), [range]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await UsageService.getSummary(toParams());
        if (!cancelled) setSummary(data);
      } catch (error) {
        if (!cancelled) toast.error("Failed to load usage.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [toParams]);

  useEffect(() => {
    if (!canViewUsers) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setUserLoading(true);
        const data = await UsageService.getByUser({
          ...toParams(),
          search: debouncedSearch || undefined,
        });
        if (!cancelled) setByUser(data);
      } catch (error) {
        if (!cancelled) toast.error("Failed to load the per-user breakdown.");
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canViewUsers, toParams, debouncedSearch]);

  return (
    <PermissionGuard permissions={[PERMISSIONS.USAGE_VIEW, PERMISSIONS.USAGE_VIEW_USERS]}>
      <div className="flex w-full flex-col gap-4 pb-4">
        {/* No engineering switch here: the tenant routes do not accept the
            query parameter and their response models have no field for the
            answer, so the control would have nothing behind it.

            Sticky for the same reason as the SuperAdmin page: the window the
            reader chose has to stay in sight over the user table. */}
        <UsageToolbar sticky range={range} onRangeChange={setRange} disabled={loading} />

        {loading && !summary ? <CenteredSpinner /> : <UsageSummary summary={summary} />}

        {canViewUsers && <UsageByUserTable data={byUser} search={search} onSearchChange={setSearch} loading={userLoading} />}
      </div>
    </PermissionGuard>
  );
};

export default AdminUsage;
